/**
 * Thin client for the `/mcp` API (M-A2) — the user's connected MCP servers for
 * Maximus. Reads/writes the same config the agents runtime uses. Tokens are
 * write-only: the server never returns them, so `McpServerView` carries only
 * `hasAuth`. Mirrors the sessionApi/billingApi shape (authHeader + requestJson
 * + friendly-error throw, no retry).
 */

import { asyncApiConfig } from '../config/async-api-config.ts';
import type { AuthUser } from '../auth/AuthContext.ts';
import { isAgentEnabledFor } from './agentAccess.ts';
import { requestJson } from './apiClient.ts';

/** The agent whose console owns the MCP surface (bring-your-own-tools). */
export const MCP_AGENT_SLUG = 'maximus';

/** ONE access rule across every MCP surface: may manage MCP servers ≡ may
 *  use Maximus. Delegates to the registry (`available: isBeta` +
 *  `requiredGroups`), so this can never drift from Maximus's own gate again
 *  — the previous admin-only literal here was narrower than both the runtime
 *  and the `/mcp` API. UX-gating only; the Lambda enforces authoritatively.
 *  Shared by the Tools-panel group, the dedicated page, and the Account
 *  pointer. */
export function canUseMcpSettings(user: AuthUser | null): boolean {
  return isAgentEnabledFor(MCP_AGENT_SLUG, user);
}

/** A server as returned by the API — never includes the stored token. The
 *  discovery fields (`status`/`tools`/…) are cached by the `/mcp` handler when a
 *  server is added/edited/refreshed; they're optional because servers saved
 *  before discovery existed carry none until re-tested. */
export interface McpServerView {
  readonly serverId: string;
  readonly name?: string;
  readonly url: string;
  readonly enabled: boolean;
  readonly hasAuth: boolean;
  /** Whether the last connection attempt initialized OK. */
  readonly status?: 'ok' | 'error';
  /** Tool names the server advertised on the last successful connect. */
  readonly tools?: readonly string[];
  readonly toolCount?: number;
  /** ISO timestamp of the last connection attempt. */
  readonly checkedAt?: string;
  /** Short reason when `status` is `'error'`. */
  readonly lastError?: string;
}

/** Fields the user supplies to add/replace a server. */
export interface McpServerInput {
  readonly serverId: string;
  readonly url: string;
  readonly name?: string;
  readonly authHeader?: string;
  readonly authToken?: string;
  readonly enabled?: boolean;
  /**
   * How the server authenticates. Absent reads exactly as it does today, so
   * existing connections keep working unmigrated; `'oauth'` is what makes the
   * runtime attach a token instead of a fixed header.
   */
  readonly auth?: 'oauth' | 'header' | 'none';
}

export async function listMcpServers(): Promise<McpServerView[]> {
  const { servers } = await requestJson<{ servers: McpServerView[] }>(
    `${asyncApiConfig.baseUrl}/mcp`,
    { logTag: 'mcpApi', friendlyError: 'Your connected tools are unavailable right now.' },
  );
  return servers;
}

export async function putMcpServer(input: McpServerInput): Promise<McpServerView[]> {
  const { servers } = await requestJson<{ servers: McpServerView[] }>(
    `${asyncApiConfig.baseUrl}/mcp`,
    {
      method: 'PUT',
      body: input,
      logTag: 'mcpApi',
      friendlyError: 'That MCP server could not be saved.',
    },
  );
  return servers;
}

export async function deleteMcpServer(serverId: string): Promise<McpServerView[]> {
  const { servers } = await requestJson<{ servers: McpServerView[] }>(
    `${asyncApiConfig.baseUrl}/mcp/${encodeURIComponent(serverId)}`,
    { method: 'DELETE', logTag: 'mcpApi', friendlyError: 'That MCP server could not be removed.' },
  );
  return servers;
}

/**
 * Begin an OAuth sign-in for an MCP server.
 *
 * Returns the provider's authorization URL; the caller sends the browser
 * there. The proof-key secret never reaches the browser — it stays in the
 * Lambda, paired with `state`, until the exchange.
 */
export async function startMcpOAuth(input: {
  serverId: string;
  url: string;
  returnTo: string;
}): Promise<{ authorizationUrl: string; state: string }> {
  return requestJson<{ authorizationUrl: string; state: string }>(
    `${asyncApiConfig.baseUrl}/mcp/oauth/start`,
    {
      method: 'POST',
      body: input,
      logTag: 'mcpApi',
      friendlyError: 'That server could not start sign-in right now.',
    },
  );
}

/**
 * Finish a sign-in after the provider returns the browser to the console.
 *
 * Runs authenticated, which is why the provider redirects HERE rather than to
 * an API callback: the pending record is scoped to one user, and no endpoint
 * has to accept an attacker-chosen `state` unauthenticated.
 */
export async function completeMcpOAuth(input: {
  code: string;
  state: string;
}): Promise<{ serverId: string; authenticated: boolean }> {
  return requestJson<{ serverId: string; authenticated: boolean }>(
    `${asyncApiConfig.baseUrl}/mcp/oauth/complete`,
    {
      method: 'POST',
      body: input,
      logTag: 'mcpApi',
      friendlyError: 'That sign-in could not be completed.',
    },
  );
}
